import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button, type ButtonProps, Snackbar } from '@parspec/pixel';

import { ImportMoreProducts } from '../ImportMoreProducts';
import { UploadedScheduleResponse, UploadedSubmittalPackageResponse } from '../ImportMoreProducts/queries/apiTypes';
import { useDatasheetAutoSearchMutation, useOmAutoSearchMutation } from '../queries';
import { isRecognisedFileFormat, isTabSupportImportProducts } from '../shared/utils';
import { useUserAccountPreferencesQuery } from '../../Settings/MyProfile/queries';
import { BOM_COPIED_MESSAGE } from '../../shared/constants';
import { TAB_VALUES } from './constants';
import useGetEmptyBods from './hooks/useGetEmptyBods';

interface ImportProductsProps extends Pick<ButtonProps, "variant" | "color" | "sx"> {
    enableMainSections?: boolean;
    enableOptionSections?: boolean;
};

const ImportProducts = (props: ImportProductsProps) => {
    const {
        enableMainSections = true,
        enableOptionSections = true,
        variant = "contained",
        color = "tertiary",
        sx,
    } = props;
    let [searchParams] = useSearchParams();

    const { bomId, projectId } = useParams();
    const navigate = useNavigate();

    const [openImportProductsModal, setOpenImportProductsModal] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const { data: userPreferences, isLoading: isUserPreferencesLoading } = useUserAccountPreferencesQuery();

    // The only purpose to use this query is to initiate the inner queries of hook
    // on render of this Component and show loader while they are not yet complete.
    const { isLoading: isLoadingBomData } = useGetEmptyBods(bomId!, null);

    const { mutateAsync: autoSearchDatasheet, isLoading: isAutoSearchDatasheetLoading } = useDatasheetAutoSearchMutation();
    const { mutateAsync: autoSearchOM, isLoading: isAutoSearchOMLoading } = useOmAutoSearchMutation();

    const toggleImportProductsModal = () => {
        setOpenImportProductsModal((old) => !old);
    };

    const onFileUploadSuccess = async (
        res: UploadedScheduleResponse | UploadedSubmittalPackageResponse,
        packageType: 'schedule' | 'submittal_package'
    ) => {
        try {
            if (packageType === 'schedule') {
                if ('file_format' in res && userPreferences?.data.autoselection && res.bods.length > 0) {
                    const data = {
                        bomId: Number(bomId),
                        input: {
                            bod_id: res.bods,
                            user_preference: userPreferences?.data.autoselection_user_group
                        }
                    };
                    setOpenImportProductsModal(false);
                    await autoSearchDatasheet(data);
                    await autoSearchOM(data);
                }
            }

            if (isRecognisedFileFormat(res)) {
                setOpenImportProductsModal(false);
                return;
            }
            const bomTab = searchParams.get("tab");
            navigate(`/v2/project/${projectId}/bom/${bomId}/addProducts`, {
                state: { bomTab: isTabSupportImportProducts(String(bomTab)) ? bomTab : TAB_VALUES.SPECIFICATION_TAB_VALUE }
            });
        } catch (err) { }
    };

    const onCopyExistingBomSuccess = () => {
        setSnackbarMessage(BOM_COPIED_MESSAGE);
        toggleImportProductsModal();
    };

    return (
        <>
            <Snackbar
                open={!!snackbarMessage}
                onClose={() => setSnackbarMessage('')}
                message={snackbarMessage}
            />

            <Button
                onClick={toggleImportProductsModal}
                color={color}
                variant={variant}
                sx={sx}
                isLoading={isUserPreferencesLoading || isAutoSearchDatasheetLoading || isAutoSearchOMLoading || isLoadingBomData}
            >
                Import Products
            </Button>

            {openImportProductsModal && (
                <ImportMoreProducts
                    open={openImportProductsModal}
                    onSuccess={onFileUploadSuccess}
                    onCloseClick={toggleImportProductsModal}
                    onCopyExistingBomSuccess={onCopyExistingBomSuccess}
                    enableMainSections={enableMainSections}
                    enableOptionSections={enableOptionSections}
                />
            )}
        </>
    );
};

export default ImportProducts;
